import styled from 'styled-components';

import { bidiProse } from 'components/bidi';

/**
 * The design's `.dl` — the detail grid.
 *
 * It replaces one-field-per-full-row, which is what every detail screen in the
 * panel did: a label, a value, a hairline, repeated down 900px of card for
 * eleven fields that between them held about forty characters.
 *
 * The columns are `auto-fit` rather than a fixed count, so the same grid gives
 * three columns at 1280 and four at 1440 without a media query, and folds to
 * one on a narrow card.
 */
export const Grid = styled.dl`
  display: grid;
  /*
   * 230px is the design file's own track floor, and the one number here that
   * is not a token — a grid track is neither type nor spacing, and a token
   * for it would have exactly this call site.
   */
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  margin: 0 -1px -1px 0;
`;

/**
 * A cell.
 *
 * Every cell draws a hairline on its right and its bottom, including the ones
 * on the outside edges — those overhang by a pixel and are clipped by the
 * card, which is why the grid carries a negative margin and why its card must
 * be `flush`. It is the only way to get "no trailing hairline" out of an
 * `auto-fit` grid, where CSS cannot say which cell ends a row.
 */
export const Item = styled.div`
  min-width: 0;
  padding: var(--s4) var(--s5);
  border-right: 1px solid var(--line);
  border-bottom: 1px solid var(--line);

  /*
   * Spans the full width — for prose, or anything that needs the room.
   *
   * The negative top margin is what closes a ragged row. An auto-fit grid
   * cannot be told which cell ends a row, so a row holding two cells in a
   * four-column grid draws its hairline under those two and stops: the rule
   * gives out halfway across the card and the last cell's right-hand
   * separator hangs in the space after it. Pulling the wide row up by a pixel
   * lands its own full-width top border exactly on that partial line, and the
   * two read as the single rule they should have been.
   *
   * A ragged row with no wide row beneath it needs nothing: its partial
   * hairline is the grid's last, and the grid's -1px bottom margin puts it
   * under the card's edge where it is clipped.
   */
  &.wide {
    grid-column: 1 / -1;
    margin-top: -1px;
    border-top: 1px solid var(--line);
  }
`;

export const Label = styled.dt`
  margin-bottom: var(--s1);
  font-family: var(--font-ui);
  font-size: var(--fs-th);
  font-weight: var(--fw-medium);
  line-height: var(--lh-body);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-faint);
`;

export const Data = styled.dd`
  margin: 0;

  /*
   * A detail row owns its line, so unlike a table column it takes the
   * alignment too: an Arabic description aligns right and an English one
   * left, both worked out from the text itself. This is the row that holds
   * every free-text field on every detail screen — subject, description,
   * note, cancel reason.
   */
  ${bidiProse};
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  line-height: var(--lh-body);
  color: var(--text);

  /* Prose — a description, a note. Reads at reading line-height, not UI. */
  &.prose {
    font-weight: var(--fw-regular);
    line-height: var(--lh-prose);
    white-space: pre-line;
  }
`;
